import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { useSelector } from "react-redux";
import {
  Floor,
  selectCurrentFloor,
  selectCurrentProject,
} from "../../../utils";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDimensionsRef, useMediaMatch } from "rooks";
import { BREAKPOINTS } from "../../../utils/styles/breakpoints";
import sanity from "../../../utils/sanity";
import InlineSVG from "react-inlinesvg";
import { COLORS } from "../../../utils/styles/colors";
import LoadingIndicator from "../../../components/LoadingIndicator/LoadingIndicator";

const Wrapper = styled.div`
  display: flex;
  width: 100%;
`;

const BuildingContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  position: relative;
  align-items: center;

  svg {
    border-radius: 25px;
    box-shadow: rgba(0, 0, 0, 0.3) 0px 19px 38px,
      rgba(0, 0, 0, 0.22) 0px 15px 12px;
    [class] {
      cursor: pointer;
      fill: transparent;
      transition: all 0.2s ease-in-out;
    }

    [class]:hover {
      fill: rgba(255, 255, 255, 0.2);
      stroke: #868788;
      stroke-width: 2;
    }
  }

  ${BREAKPOINTS.LARGE} {
    pointer-events: auto;
  }
`;

interface Props {
  onFloorSelected: (floor: string) => void;
  onActiveFloorChanged: (floor?: Floor) => void;
  activeFloor?: Floor;
}

export const FloorSelector: React.FunctionComponent<Props> = ({
  onFloorSelected,
  onActiveFloorChanged,
  activeFloor,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [ref, dimensions] = useDimensionsRef();
  const [loading, setLoading] = useState(true);

  const currentProject = useSelector(selectCurrentProject);
  const svgRef = useRef<SVGElement>(null);

  useEffect(() => setLoading(!svgRef.current), [svgRef.current]);

  const buildingClickHandler = (event: React.MouseEvent) => {
    const container = (event.target as Element).classList[0];
    if (container) {
      const floor = currentProject?.floors.find(
        (a) => a.container === container
      );
      floor && onFloorSelected(floor.floor.floor);
    }
  };

  useEffect(() => {
    if (!loading && containerRef.current) {
      const container = containerRef.current;
      const svg = container.querySelector("svg");
      const floors = Array.from(svg!.querySelectorAll("[class]"));

      const label = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "text"
      );
      label.setAttribute("id", "floor-label");
      label.setAttribute("text-anchor", "middle");
      label.setAttribute("fill", "#ffffff");
      label.setAttribute("font-size", "32");
      label.setAttribute("font-weight", "bold");
      label.setAttribute("pointer-events", "none");
      label.setAttribute(
        "style",
        "text-shadow: 0px 1px 4px rgba(19, 15, 15, 0.5)"
      );
      label.setAttribute("font-family", "Inter, sans-serif");
      svg?.appendChild(label);

      const onMouseEnter = (
        floorEl: SVGGraphicsElement,
        floorNumber: string
      ) => {
        const box = floorEl.getBBox();
        label.setAttribute("x", (box.x + box.width / 2).toString());
        label.setAttribute("y", (box.y + box.height / 2 + 6).toString()); // +6 to visually center
        label.textContent = floorNumber;
        label.style.display = "block";
      };

      const onMouseLeave = () => {
        label.style.display = "none";
      };

      floors.forEach((floorEl) => {
        const className = floorEl.classList[0];
        const floor = currentProject?.floors.find(
          (f) => f.container === className
        );
        if (!floor) return;

        floorEl.addEventListener("mouseenter", () =>
          onMouseEnter(floorEl as SVGGraphicsElement, floor.floor.floor)
        );
        floorEl.addEventListener("mouseleave", onMouseLeave);
      });

      return () => {
        label.remove();
      };
    }
    return undefined;
  }, [loading, currentProject]);

  const url = sanity.imageUrlBuilder(currentProject!.image.asset._ref).url();

  return (
    <Wrapper ref={containerRef}>
      <BuildingContainer ref={ref} onClick={buildingClickHandler}>
        {!!dimensions?.width && (
          <InlineSVG
            innerRef={svgRef}
            src={url}
            onLoad={() => setLoading(false)}
            loader={<LoadingIndicator />}
            width="100%"
            preserveAspectRatio="none"
          />
        )}
      </BuildingContainer>
    </Wrapper>
  );
};
